import { navigationEpoch } from '../../navigation/loadEpoch';
import { getPersistenceNamespace } from '../helpers/persistenceHelpers';
import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import {
  Workflow,
  WorkflowSummary,
  NodeTypeDescriptor,
  Execution,
  RunRefusal,
  runRefusal,
  GraphNode,
  GraphEdge,
  TriggerConfig,
  listWorkflows as apiList,
  getWorkflow as apiGet,
  createWorkflow as apiCreate,
  updateWorkflow as apiUpdate,
  deleteWorkflow as apiDelete,
  runWorkflow as apiRun,
  getRun as apiGetRun,
  getNodeTypes as apiNodeTypes,
} from '../../components/Workflows/api';

const genId = (prefix: string) =>
  `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const normalizeWorkflow = (wf: Workflow): Workflow => {
  const raw: any = wf?.steps;
  let steps =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? {
          nodes: Array.isArray(raw.nodes) ? raw.nodes : [],
          edges: Array.isArray(raw.edges) ? raw.edges : [],
          trigger: raw.trigger || null,
          docs_url: raw.docs_url || null,
        }
      : { nodes: [], edges: [], trigger: null, docs_url: null };

  // Ensure every graph has exactly one start.trigger node as its anchor.
  const hasStart = steps.nodes.some((n: any) => n.type === 'start.trigger');
  if (!hasStart) {
    const startNode = {
      id: 'n_start',
      type: 'start.trigger',
      config: {},
      position: { x: 80, y: 200 },
    };
    steps = { ...steps, nodes: [startNode, ...steps.nodes] };
  }
  return { ...wf, steps };
};

/** The step worth showing first: a destination with output, else the last step that produced one. */
export const preferredStep = (execution: Execution, nodes: GraphNode[]): string | null => {
  const nodeById: Record<string, string> = {};
  nodes.forEach((n) => { nodeById[n.id] = n.type; });
  const withOutput = Object.keys(execution.step_results || {})
    .filter((id) => execution.step_results[id].status === 'ok');
  return withOutput.find((id) => (nodeById[id] || '').startsWith('destination.'))
    || withOutput[withOutput.length - 1]
    || null;
};

export interface WorkflowsSlice {
  workflowsList: WorkflowSummary[];
  nodeTypes: NodeTypeDescriptor[];
  currentWorkflow: Workflow | null;
  selectedNodeId: string | null;
  connectingFromNodeId: string | null;
  lastExecution: Execution | null;
  /**
   * The last run this caller asked for and was REFUSED (#426 r26).
   *
   * `POST /{id}/run` can answer `403 run_withheld`: the run happened, and its record belongs to another
   * account (#426 r25). The store used to drop that answer on the floor, so the panel went on showing
   * the previous run's document under "Last run", or "No runs yet — click Run." — a refusal displayed
   * as an absence, which is the same class the server spent five rounds taking out of its own answers.
   * It is kept apart from `lastExecution` because it is not one: there is nothing of the run to show.
   */
  lastRunRefusal: RunRefusal | null;
  /** A link's step that is in neither the graph nor the run (#516): the default step is shown with a note. */
  wfMissingStep: { workflowId: string; stepId: string } | null;
  isRunning: boolean;
  isDirty: boolean;

  wfLoadList: () => Promise<void>;
  wfLoadNodeTypes: () => Promise<void>;
  wfOpen: (id: string, signal?: AbortSignal) => Promise<void>;
  wfNew: (name: string) => Promise<string>;
  wfDelete: (id: string) => Promise<void>;
  wfClose: () => void;
  /** Show one past run of the open workflow (#472); false when it is not this workflow's or not readable. */
  wfShowRun: (executionId: string, signal?: AbortSignal) => Promise<boolean>;

  wfUpdateNode: (id: string, patch: Partial<GraphNode>) => void;
  wfUpdateNodeConfig: (id: string, config: Record<string, any>) => void;
  wfDeleteNode: (id: string) => void;
  wfSelectNode: (id: string | null) => void;
  wfStartConnect: (id: string) => void;
  wfCompleteConnect: (targetId: string) => void;
  wfCancelConnect: () => void;
  wfDeleteEdge: (id: string) => void;

  wfSave: () => Promise<void>;
  wfRun: () => Promise<void>;
  wfRename: (name: string) => void;
  wfSetTrigger: (trigger: TriggerConfig | null) => void;
  wfSetDocsUrl: (url: string) => void;

  // Insert a placeholder node after an existing one and auto-connect.
  wfInsertAfter: (sourceNodeId: string) => void;
  // Set the concrete type for a placeholder node.
  wfSetNodeType: (nodeId: string, type: string) => void;
  // Replace the upstream set of a node (rewires edges targeting this node).
  wfSetUpstream: (nodeId: string, upstreamIds: string[]) => void;
}

export const createWorkflowsSlice: StateCreator<
  VoyagerState, [], [], WorkflowsSlice
> = (set, get) => ({
  workflowsList: [],
  nodeTypes: [],
  currentWorkflow: null,
  selectedNodeId: null,
  connectingFromNodeId: null,
  lastExecution: null,
  lastRunRefusal: null,
  wfMissingStep: null,
  isRunning: false,
  isDirty: false,

  wfLoadList: async () => {
    try {
      const list = await apiList();
      set({ workflowsList: Array.isArray(list) ? list : [] });
    } catch {
      set({ workflowsList: [] });
    }
  },

  wfLoadNodeTypes: async () => {
    if (get().nodeTypes.length) return;
    try {
      const types = await apiNodeTypes();
      set({ nodeTypes: Array.isArray(types) ? types : [] });
    } catch {
      set({ nodeTypes: [] });
    }
  },

  wfOpen: async (id: string, signal?: AbortSignal) => {
    const namespace = getPersistenceNamespace();
    const loadEpoch = navigationEpoch();
    const wf = normalizeWorkflow(await apiGet(id));
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
    const startNode = wf.steps.nodes.find((n) => n.type === 'start.trigger');
    set({
      currentWorkflow: wf,
      selectedNodeId: startNode?.id || null,
      connectingFromNodeId: null,
      lastExecution: null,
      lastRunRefusal: null,
      wfMissingStep: null,
      isDirty: false,
    });
  },

  wfNew: async (name: string) => {
    const wf = normalizeWorkflow(await apiCreate({ name, steps: { nodes: [], edges: [] } }));
    const startNode = wf.steps.nodes.find((n) => n.type === 'start.trigger');
    set((s) => ({
      currentWorkflow: wf,
      workflowsList: [
        {
          id: wf.id,
          name: wf.name,
          description: wf.description,
          status: wf.status,
          node_count: 0,
          node_types: [],
          updated_at: wf.updated_at,
        },
        ...s.workflowsList,
      ],
      selectedNodeId: startNode?.id || null,
      lastExecution: null,
      lastRunRefusal: null,
      wfMissingStep: null,
      isDirty: false,
    }));
    return wf.id;
  },

  wfDelete: async (id: string) => {
    await apiDelete(id);
    set((s) => ({
      workflowsList: s.workflowsList.filter((w) => w.id !== id),
      currentWorkflow: s.currentWorkflow?.id === id ? null : s.currentWorkflow,
    }));
  },

  wfClose: () => {
    set({
      currentWorkflow: null,
      selectedNodeId: null,
      connectingFromNodeId: null,
      lastExecution: null,
      lastRunRefusal: null,
      wfMissingStep: null,
      isDirty: false,
    });
    // Refresh the list so newly saved/renamed workflows show up.
    get().wfLoadList().catch(() => {});
  },

  wfShowRun: async (executionId, signal) => {
    const namespace = getPersistenceNamespace();
    const loadEpoch = navigationEpoch();
    const wf = get().currentWorkflow;
    if (!wf) return false;
    let execution: Execution;
    try {
      execution = await apiGetRun(executionId);
    } catch {
      return false;
    }
    if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return false;
    if (execution?.workflow_id !== wf.id || get().currentWorkflow?.id !== wf.id) return false;
    set({
      lastExecution: execution,
      selectedNodeId: preferredStep(execution, get().currentWorkflow?.steps.nodes || []),
      lastRunRefusal: null,
      wfMissingStep: null,
    });
    return true;
  },

  wfUpdateNode: (id, patch) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    set({
      currentWorkflow: {
        ...wf,
        steps: {
          ...wf.steps,
          nodes: wf.steps.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
        },
      },
      isDirty: true,
    });
  },

  wfUpdateNodeConfig: (id, config) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    set({
      currentWorkflow: {
        ...wf,
        steps: {
          ...wf.steps,
          nodes: wf.steps.nodes.map((n) =>
            n.id === id ? { ...n, config: { ...n.config, ...config } } : n
          ),
        },
      },
      isDirty: true,
    });
  },

  wfDeleteNode: (id) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    set({
      currentWorkflow: {
        ...wf,
        steps: {
          nodes: wf.steps.nodes.filter((n) => n.id !== id),
          edges: wf.steps.edges.filter((e) => e.source !== id && e.target !== id),
        },
      },
      selectedNodeId: null,
      isDirty: true,
    });
  },

  wfSelectNode: (id) => set({ selectedNodeId: id, wfMissingStep: null }),
  wfStartConnect: (id) => set({ connectingFromNodeId: id }),
  wfCancelConnect: () => set({ connectingFromNodeId: null }),

  wfCompleteConnect: (targetId) => {
    const { currentWorkflow, connectingFromNodeId } = get();
    if (!currentWorkflow || !connectingFromNodeId || connectingFromNodeId === targetId) {
      set({ connectingFromNodeId: null });
      return;
    }
    const exists = currentWorkflow.steps.edges.some(
      (e) => e.source === connectingFromNodeId && e.target === targetId
    );
    if (exists) {
      set({ connectingFromNodeId: null });
      return;
    }
    const edge: GraphEdge = {
      id: genId('e'),
      source: connectingFromNodeId,
      target: targetId,
    };
    set({
      currentWorkflow: {
        ...currentWorkflow,
        steps: {
          ...currentWorkflow.steps,
          edges: [...currentWorkflow.steps.edges, edge],
        },
      },
      connectingFromNodeId: null,
      isDirty: true,
    });
  },

  wfDeleteEdge: (id) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    set({
      currentWorkflow: {
        ...wf,
        steps: {
          ...wf.steps,
          edges: wf.steps.edges.filter((e) => e.id !== id),
        },
      },
      isDirty: true,
    });
  },

  wfSave: async () => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    const updated = normalizeWorkflow(await apiUpdate(wf.id, {
      name: wf.name,
      description: wf.description,
      steps: wf.steps,
    }));
    set({ currentWorkflow: updated, isDirty: false });
    await get().wfLoadList();
  },

  wfRun: async () => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    set({ isRunning: true });
    try {
      if (get().isDirty) {
        await get().wfSave();
      }
      let execution: Execution;
      try {
        execution = await apiRun(wf.id, {});
      } catch (err) {
        const refused = runRefusal(err);
        if (!refused) throw err;
        // The run HAPPENED and is not this caller's to read. What is on screen belongs to an earlier
        // run, so it is taken down with the refusal put in its place: leaving it there would answer
        // "Run" with somebody else's output, and clearing it alone would answer with "no runs yet".
        set({ lastExecution: null, selectedNodeId: null, lastRunRefusal: refused });
        return;
      }
      // Auto-select the most useful node to preview.
      const preferred = preferredStep(execution, get().currentWorkflow?.steps.nodes || []);
      set({ lastExecution: execution, selectedNodeId: preferred, lastRunRefusal: null });
    } finally {
      set({ isRunning: false });
    }
  },

  wfRename: (name: string) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    set({
      currentWorkflow: { ...wf, name },
      isDirty: true,
    });
  },

  wfSetTrigger: (trigger) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    set({
      currentWorkflow: {
        ...wf,
        steps: { ...wf.steps, trigger: trigger || null },
      },
      isDirty: true,
    });
  },

  wfSetDocsUrl: (url: string) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    set({
      currentWorkflow: {
        ...wf,
        steps: { ...wf.steps, docs_url: url || null },
      },
      isDirty: true,
    });
  },

  wfInsertAfter: (sourceNodeId: string) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    const src = wf.steps.nodes.find((n) => n.id === sourceNodeId);
    if (!src) return;
    const newNode: GraphNode = {
      id: genId('n'),
      type: '', // placeholder — inspector will let the user pick
      config: {},
      position: {
        x: src.position.x + 260,
        y: src.position.y + (Math.random() * 40 - 20),
      },
    };
    const newEdge: GraphEdge = {
      id: genId('e'),
      source: sourceNodeId,
      target: newNode.id,
    };
    set({
      currentWorkflow: {
        ...wf,
        steps: {
          ...wf.steps,
          nodes: [...wf.steps.nodes, newNode],
          edges: [...wf.steps.edges, newEdge],
        },
      },
      selectedNodeId: newNode.id,
      isDirty: true,
    });
  },

  wfSetNodeType: (nodeId: string, type: string) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    set({
      currentWorkflow: {
        ...wf,
        steps: {
          ...wf.steps,
          nodes: wf.steps.nodes.map((n) =>
            n.id === nodeId ? { ...n, type, config: {} } : n
          ),
        },
      },
      isDirty: true,
    });
  },

  wfSetUpstream: (nodeId: string, upstreamIds: string[]) => {
    const wf = get().currentWorkflow;
    if (!wf) return;
    const otherEdges = wf.steps.edges.filter((e) => e.target !== nodeId);
    const newEdges = upstreamIds.map((src) => ({
      id: genId('e'),
      source: src,
      target: nodeId,
    }));
    set({
      currentWorkflow: {
        ...wf,
        steps: { ...wf.steps, edges: [...otherEdges, ...newEdges] },
      },
      isDirty: true,
    });
  },
});
